import React from 'react';
import styles from './GlobalButton.module.css';

type ButtonVariant = 'primary' | 'secondary';
type IconPosition = 'left' | 'right';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: IconPosition;
  autoWidth?: boolean;
  destructive?: boolean;
}

const GlobalButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = '',
      variant = 'primary',
      isLoading = false,
      disabled,
      icon,
      iconPosition = 'left',
      autoWidth,
      destructive = false,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;
    const buttonClasses = [
      styles.button,
      styles[variant],
      isLoading ? styles.loading : '',
      autoWidth ? styles.autoWidth : '',
      destructive ? styles.destructive : '',
      className,
    ].join(' ');

    const renderIcon = () => {
      if (!icon) return null;
      const iconClass = `${styles.icon} ${
        iconPosition === 'left' ? styles.iconLeft : styles.iconRight
      }`;
      return <span className={iconClass} aria-hidden="true">{icon}</span>;
    };

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading ? (
          <div className={styles.loadingContent}>
            <span className={styles.loadingText}>Loading...</span>
          </div>
        ) : (
          <>
            {iconPosition === 'left' && renderIcon()}
            <span>{children}</span>
            {iconPosition === 'right' && renderIcon()}
          </>
        )}
      </button>
    );
  }
);

GlobalButton.displayName = 'GlobalButton';

export { GlobalButton };
